<template>
    <div class="row">
        <div class="col-12 mb-1">
            <button class="btn btn-sm btn-flat-primary p-0" v-on:click="showHelp = !showHelp">
                <feather-icon icon="HelpCircleIcon" size="14" class="mr-25"></feather-icon>
                Nasıl çalışır?
                <feather-icon :icon="showHelp ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="12"></feather-icon>
            </button>
            <div v-if="showHelp" class="border rounded p-1 mt-50 bg-light">
                <small>
                    <b>Baz tarih nedir?</b><br>
                    Fiyatın <u>hangi mazot seviyesinde</u> verildiğini gösterir. Sözleşme tarihinden
                    bağımsızdır. Fiyatın hangi gün verildiği hatırlanmasa da hangi mazot seviyesinde
                    verildiği bilinir; sistem de zammı bu noktadan ölçer.
                    <hr class="my-50">

                    <b>Excel dosyası</b><br>
                    Kolonlar: <code>BaslangicBolgeKodu</code>, <code>BitisBolgeKodu</code>,
                    <code>TasinanUrunStokKodu</code>, <code>AracTipiKodu</code>,
                    <code>TasimaDurumu</code>, <code>Fiyat</code>, <code>BazTarih</code>.<br>
                    <code>BazTarih</code> <b>opsiyoneldir</b>: boş bırakılan satırlar yukarıda seçtiğiniz
                    varsayılan baz tarihe düşer. Kolonu hiç olmayan eski dosyalar da çalışmaya devam eder.
                    Tarihi <code>gg.aa.yyyy</code> yazabilir ya da hücreyi tarih biçiminde bırakabilirsiniz.
                    <hr class="my-50">

                    <b>Kaydedince ne oluyor?</b><br>
                    Girdiğiniz fiyat, baz tarihte geçerli olan fiyat kabul edilir. Baz tarihten bugüne
                    kadar mazot eşiği aşan her noktada bir <b>ara zam</b> hesaplanır ve geçmişe fiyat
                    satırı olarak yazılır. Böylece geç girilen rota, zamanında girilmiş gibi davranır ve
                    aynı güzergâhtaki diğer nakliyecilerle aynı fiyat davranışını gösterir.
                    <hr class="my-50">

                    <b>Kaydetmeden önce görün</b><br>
                    <b>Güncel Fiyatları Hesapla</b> ile her rotanın güncel fiyatı, kaç ara zam oluşacağı
                    ve toplamda kaç fiyat satırı yazılacağı listelenir. Ara zamların gün gün detayı için
                    "N ara zam" yazısına tıklayın.
                    <hr class="my-50">

                    <b>Hata olursa</b><br>
                    Satırlardan biri bile hatalıysa <b>hiçbir rota kaydedilmez</b>; yarım yüklenmiş dosya
                    kalmaz. Sözleşmede zaten var olan bir rota dosyada tekrar geçerse hata verir.
                    <hr class="my-50">

                    <b class="text-warning">Dikkat</b><br>
                    Geçmişe yazılan ara zam satırları, o tarihlerden sonraki seferlerin <b>maliyet
                    raporunu</b> da etkiler. Faturası kesilmiş taşımalar varsa rapor faturayla ayrışabilir.
                </small>
            </div>
        </div>

        <!-- Adım 1: dosya. Excel'deki BazTarih kolonu opsiyoneldir; boş satırlar aşağıda
             seçilen varsayılan baz tarihe düşer. -->
        <div class="col-12 mb-1">
            <label class="form-label">Excel Dosyası</label>
            <v-file-uploader :accept="['xlsx']" v-model="excelFile" valueParameter="File"></v-file-uploader>
            <div class="d-flex mt-50">
                <button class="btn btn-sm btn-flat-secondary mr-50" v-on:click="downloadSample">
                    <feather-icon icon="DownloadIcon" size="14" class="mr-25"></feather-icon>
                    Örnek Dosya
                </button>
                <v-executer
                    variant="primary"
                    :action="uploadFile"
                    :loading="uploading"
                    :disabled="excelFile.File == null">
                    Dosyayı Oku
                </v-executer>
            </div>
        </div>

        <div class="col-12 mb-1">
            <label class="form-label">
                Mazot Baz Tarihi <span class="text-danger">*</span>
                <small class="text-muted">(BazTarih kolonu boş olan satırlar için)</small>
            </label>
            <v-select-list
                v-model="fuelBase"
                placeholder="Baz tarih ve mazot fiyatı seçiniz"
                label="Label"
                :loading="fuelPricesLoading"
                :options="fuelPrices"/>
            <small v-if="needsDefaultBaseDate && !defaultFuelBaseDate" class="text-danger">
                Dosyada BazTarih kolonu boş olan satırlar var; varsayılan baz tarih seçilmelidir.
            </small>
            <small v-else-if="routes.length && !needsDefaultBaseDate" class="text-success">
                Tüm satırlar kendi baz tarihini taşıyor.
            </small>
        </div>

        <!-- Hatalı satırlar: mevcut Excel doğrulamasının çıktısı. -->
        <div class="col-12" v-if="invalidRoutes.length || nullValueRoutes.length">
            <b-alert show variant="danger" class="p-1">
                <b>{{ invalidRoutes.length + nullValueRoutes.length }}</b> satırda hata var.
                Düzeltip tekrar yükleyiniz.
                <ul class="mb-0 mt-50 pl-2">
                    <li v-for="(r,i) in invalidRoutes" :key="'i'+i">
                        <small>
                            {{ r.BaslangicBolgeKodu }} → {{ r.BitisBolgeKodu }} ({{ r.TasinanUrunStokKodu }}):
                            {{ (r.Errors || []).join(' ') }}
                        </small>
                    </li>
                    <li v-for="(r,i) in nullValueRoutes" :key="'n'+i">
                        <small>Eksik alan içeren satır: {{ r.BaslangicBolgeKodu }} → {{ r.BitisBolgeKodu }}</small>
                    </li>
                </ul>
            </b-alert>
        </div>

        <div class="col-12" v-if="routes.length">
            <div class="d-flex justify-content-between align-items-center mb-50">
                <h5 class="mb-0">Okunan Rotalar ({{ routes.length }})</h5>
                <v-executer
                    variant="primary"
                    :action="simulate"
                    :loading="simulating"
                    :disabled="needsDefaultBaseDate && !defaultFuelBaseDate">
                    Güncel Fiyatları Hesapla
                </v-executer>
            </div>

            <b-alert v-if="simulation" show variant="info" class="p-1">
                <b>{{ simulation.RouteCount }}</b> rota ·
                <b>{{ simulation.TotalStepCount }}</b> geriye dönük ara zam ·
                kaydedildiğinde toplam <b>{{ simulation.TotalRowCount }}</b> fiyat satırı yazılacak.
                Toplam: {{ simulation.TotalStartPriceStr }} ₺ → <b>{{ simulation.TotalFinalPriceStr }} ₺</b>
            </b-alert>

            <div class="table-responsive">
                <table class="table table-sm table-bordered mb-0">
                    <thead>
                        <tr>
                            <th class="text-nowrap">Başlangıç</th>
                            <th class="text-nowrap">Bitiş</th>
                            <th class="text-nowrap">Araç</th>
                            <th class="text-nowrap">Ürün</th>
                            <th class="text-nowrap">Taşıma</th>
                            <th class="text-nowrap">Baz Tarih</th>
                            <th class="text-nowrap text-right">Fiyat</th>
                            <th class="text-nowrap text-right">Güncel Fiyat</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(r,i) in routes">
                        <tr :key="i">
                            <td class="text-nowrap">{{ r.StartRegionName }}</td>
                            <td class="text-nowrap">{{ r.EndRegionName }}</td>
                            <td class="text-nowrap">{{ r.VehicleTypeName }}</td>
                            <td class="text-nowrap">{{ r.StockName }}</td>
                            <td class="text-nowrap">{{ r.VehicleTransportStatusName }}</td>
                            <td class="text-nowrap">
                                <span v-if="r.FuelBaseDateStr">{{ r.FuelBaseDateStr }}</span>
                                <small v-else class="text-muted">varsayılan</small>
                            </td>
                            <td class="text-nowrap text-right">{{ r.PriceStr }} ₺</td>
                            <td class="text-nowrap text-right">
                                <template v-if="resultByRow[i]">
                                    <span v-if="resultByRow[i].ErrorMessage" class="text-danger">
                                        <small>{{ resultByRow[i].ErrorMessage }}</small>
                                    </span>
                                    <template v-else>
                                        <b>{{ resultByRow[i].Escalation.FinalPriceStr }} ₺</b>
                                        <button
                                            v-if="resultByRow[i].Escalation.Steps.length"
                                            class="btn btn-link btn-sm p-0 d-block ml-auto"
                                            v-on:click="toggleSteps(i)">
                                            <small>
                                                {{ resultByRow[i].Escalation.Steps.length }} ara zam
                                                ({{ resultByRow[i].Escalation.TotalChangeRateStr }})
                                                <feather-icon
                                                    :icon="isExpanded(i) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                                                    size="12"></feather-icon>
                                            </small>
                                        </button>
                                        <small v-else class="d-block text-muted">ara zam yok</small>
                                    </template>
                                </template>
                                <small v-else class="text-muted">-</small>
                            </td>
                        </tr>
                        <tr v-if="isExpanded(i) && resultByRow[i] && resultByRow[i].Escalation" :key="i + '-steps'">
                            <td colspan="100" class="bg-light p-1">
                                <small class="text-muted d-block mb-50">
                                    {{ r.StartRegionName }} → {{ r.EndRegionName }} ·
                                    baz {{ resultByRow[i].Escalation.BaseDateStr }}
                                    ({{ resultByRow[i].Escalation.BaseFuelPriceStr }} ₺) ·
                                    girilen fiyat {{ resultByRow[i].Escalation.StartPriceStr }} ₺
                                </small>
                                <table class="table table-sm table-bordered bg-white mb-0">
                                    <thead>
                                        <tr>
                                            <th class="text-nowrap">Tarih</th>
                                            <th class="text-nowrap text-right">Mazot</th>
                                            <th class="text-nowrap text-right">Değişim</th>
                                            <th class="text-nowrap text-right">Eski Fiyat</th>
                                            <th class="text-nowrap text-right">Yeni Fiyat</th>
                                            <th class="text-nowrap text-right">Fark</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(s,si) in resultByRow[i].Escalation.Steps" :key="si">
                                            <td class="text-nowrap">{{ s.DateStr }}</td>
                                            <td class="text-nowrap text-right">{{ s.PreviousFuelPriceStr }} → {{ s.FuelPriceStr }}</td>
                                            <td class="text-nowrap text-right">{{ s.FuelChangeRateStr }}</td>
                                            <td class="text-nowrap text-right">{{ s.OldPriceStr }}</td>
                                            <td class="text-nowrap text-right font-weight-bolder">{{ s.NewPriceStr }}</td>
                                            <td class="text-nowrap text-right">{{ s.PriceDeltaStr }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Sunucudan dönen satır bazlı kayıt hataları. Bir satır bile hatalıysa
             hiçbiri kaydedilmez; kullanıcının bunu görmesi gerekir. -->
        <div class="col-12 mt-1" v-if="saveErrors.length">
            <b-alert show variant="danger" class="p-1 mb-0">
                <b>Kayıt yapılmadı.</b>
                <ul class="mb-0 mt-50 pl-2">
                    <li v-for="(e,i) in saveErrors" :key="i"><small>{{ e.StockCode }}: {{ e.Message }}</small></li>
                </ul>
            </b-alert>
        </div>
    </div>
</template>

<script>
import { AgreementApi } from '@/api/agreement'
import { FuelPriceApi } from '@/api/fuel-price'
import VFileUploader from '@/components/v-file-uploader/VFileUploader.vue'
import VExecuter from '@/components/VExecuter.vue'
import Additional from '@/utilities/additional'
import { BAlert } from 'bootstrap-vue'
import moment from 'moment'

export default {
    components:{
        VFileUploader,
        VExecuter,
        BAlert
    },
    props:{
        agreementId:String
    },
    data(){
        return {
            excelFile:{ File:null },
            uploading:false,
            routes:[],
            invalidRoutes:[],
            nullValueRoutes:[],
            fuelPrices:[],
            fuelPricesLoading:false,
            defaultFuelBaseDate:null,
            simulation:null,
            simulating:false,
            saveErrors:[],
            expandedRows:[],
            showHelp:false
        }
    },
    mounted(){
        this.getFuelPrices()
    },
    watch:{
        defaultFuelBaseDate(){
            this.simulation = null
            this.expandedRows = []
        },
        canSave:{
            immediate:true,
            handler(value){
                this.$emit('ready',value)
            }
        }
    },
    methods:{
        reset(){
            this.excelFile = { File:null }
            this.routes = []
            this.invalidRoutes = []
            this.nullValueRoutes = []
            this.simulation = null
            this.expandedRows = []
            this.saveErrors = []
            this.defaultFuelBaseDate = null
        },
        toggleSteps(key){
            const index = this.expandedRows.indexOf(key)
            if(index >= 0)
                this.expandedRows.splice(index,1)
            else
                this.expandedRows.push(key)
        },
        isExpanded(key){
            return this.expandedRows.indexOf(key) >= 0
        },
        getFuelPrices(){
            this.fuelPricesLoading = true
            FuelPriceApi.List().then(res=>{
                this.fuelPrices = res.data.Data || []
                this.fuelPricesLoading = false
            }).catch(err=>{
                console.error(err)
                this.fuelPricesLoading = false
            })
        },
        downloadSample(){
            AgreementApi.DownloadSampleFile().then(res=>{
                Additional.DownloadFile(res.data, 'Sözleşme Rota Şablonu.xlsx')
            })
        },
        async uploadFile(){
            this.uploading = true
            this.routes = []
            this.invalidRoutes = []
            this.nullValueRoutes = []
            this.simulation = null
            this.expandedRows = []
            this.saveErrors = []

            const data = await Additional.ToFormData(this.excelFile)
            AgreementApi.UploadExcel(data).then(res=>{
                const payload = res.data.Data || {}
                this.routes = payload.ValidRoutes || []
                this.invalidRoutes = payload.InvalidRoutes || []
                this.nullValueRoutes = payload.HasNullValueRoutes || []
                if(!res.data.Success)
                    this.$result(res)
                this.uploading = false
            }).catch(err=>{
                this.$result(null,err)
                this.uploading = false
            })
        },
        simulate(){
            if(!this.routes.length || (this.needsDefaultBaseDate && !this.defaultFuelBaseDate))
                return

            this.simulating = true
            this.simulation = null
            this.expandedRows = []
            AgreementApi.SimulateRoutes({
                AgreementId: this.agreementId,
                DefaultFuelBaseDate: this.defaultFuelBaseDate,
                Routes: this.routes.map((r,i) => ({
                    RowKey: String(i),
                    StartRegionName: r.StartRegionName,
                    EndRegionName: r.EndRegionName,
                    StockCode: r.StockCode,
                    VehicleTypeName: r.VehicleTypeName,
                    Price: r.PriceValue,
                    FuelBaseDate: r.FuelBaseDate || null
                }))
            }).then(res=>{
                if(res.data.Success)
                    this.simulation = res.data.Data
                else
                    this.$result(res)
                this.simulating = false
            }).catch(err=>{
                this.$result(null,err)
                this.simulating = false
            })
        },
        // Üst ekran tarafından çağrılır; kaydın sonucunu promise olarak döner.
        save(){
            this.saveErrors = []
            return AgreementApi.AddRoutes({
                AgreementId: this.agreementId,
                DefaultFuelBaseDate: this.defaultFuelBaseDate,
                Routes: this.routes.map((r,i) => ({
                    RowKey: String(i),
                    StartRegionId: r.StartRegionId,
                    EndRegionId: r.EndRegionId,
                    VehicleTypeId: r.VehicleTypeId,
                    Status: r.VehicleTransportStatus,
                    StockCode: r.StockCode,
                    StockName: r.StockName,
                    Price: r.PriceValue,
                    FuelBaseDate: r.FuelBaseDate || null
                }))
            }).then(res=>{
                if(!res.data.Success && res.data.Data)
                    this.saveErrors = res.data.Data.Errors || []
                return res
            })
        }
    },
    computed:{
        // Varsayılan baz tarih yalnızca BazTarih kolonu boş olan satırlar için gerekli;
        // dosyanın tamamı kendi baz tarihini taşıyorsa kullanıcıdan istemeye gerek yok.
        needsDefaultBaseDate(){
            return this.routes.some(r => !r.FuelBaseDate)
        },
        canSave(){
            return this.routes.length > 0
                && (!this.needsDefaultBaseDate || this.defaultFuelBaseDate != null)
                && this.invalidRoutes.length === 0
                && this.nullValueRoutes.length === 0
        },
        fuelBase:{
            get:function(){
                if(!this.defaultFuelBaseDate)
                    return null
                return this.fuelPrices.find(f => moment(f.Date).isSame(moment(this.defaultFuelBaseDate),'day')) || null
            },
            set:function(value){
                this.defaultFuelBaseDate = value == null ? null : moment(value.Date).format('YYYY-MM-DD')
            }
        },
        resultByRow(){
            const map = {}
            if(this.simulation && this.simulation.Routes){
                this.simulation.Routes.forEach(r => { map[r.RowKey] = r })
            }
            return map
        }
    }
}
</script>
